import { Box, Text, Button, IconButton, Stack, Flex, HStack, Divider, Badge, useToast } from "@chakra-ui/react";
import { DeleteIcon, TimeIcon } from "@chakra-ui/icons";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import CartSummaryPayment from './CartSummaryPayment';

const CartList = ({ cartData, userData }) => {
  const [cartItems, setCartItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const containerRef = useRef(null);
  const toast = useToast();

  const navigate = useNavigate();
  // Extract user profile data
  useEffect(() => {
    if (userData && userData.data) {
      setUserProfile(userData.data);
      console.log("User profile data:", userData.data);
    }
  }, [userData]);

  const handleContinueBooking = () => {
    navigate("/booking"); // replace "/booking" with the path to your booking page
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (cartData) {
      const sortedItems = cartData.times.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setCartItems(sortedItems);
    }
    
  }, [cartData]);

  useEffect(() => {
    let newTotal = 0;
    cartItems.forEach(item => {
      newTotal += item.price;
    });
    setTotal(newTotal);
  }, [cartItems]);

  const handleRemoveItem = (itemToRemove) => {
    setCartItems(cartItems.filter(item => item.start_time !== itemToRemove.start_time));
  };

  // Let's also add a scroll-to-top function for the TimeSlots component
  const handleCheckout = () => {
    navigate('/checkout');
    // The useEffect will handle scrolling to top when the CartList component mounts
  };

  return (
    <Box 
      ref={containerRef}
      maxWidth={["100%", "100%", "960px"]} 
      margin="0 auto" 
      padding={["2%", "3%", "5% 0"]} 
      marginBottom="5em"
    >
      <Box 
        borderRadius="lg"
        p={4}
        bg="white"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
        mb={6}
      >
        <Flex 
          padding="1em 0" 
          borderBottom="1px solid #f0f0f0" 
          display="flex" 
          alignItems="center"
          mb={4}
        >
          <Text 
            as="h1" 
            fontFamily="'Droid Serif', serif" 
            fontSize={["1.5em", "1.75em", "2em"]}
            fontWeight="bold"
            color="gray.800"
          >
            Your Cart
          </Text>
          <Button 
            onClick={handleContinueBooking} 
            textDecoration="none" 
            fontFamily="'Montserrat', sans-serif" 
            letterSpacing="-0.015em" 
            fontSize={[".7em", ".8em", ".85em"]} 
            py={5}
            px={6}
            color="#fff" 
            bg="#82ca9c" 
            fontWeight="bold" 
            borderRadius="full" 
            marginLeft="auto" 
            boxShadow="0 2px 4px rgba(130, 202, 156, 0.3)"
            _hover={{ 
              background: '#6db889',
              boxShadow: "0 4px 8px rgba(130, 202, 156, 0.4)",
              transform: "translateY(-2px)"
            }}
            transition="all 0.2s"
          >
            Continue Booking
          </Button>
        </Flex>
        
        <Box padding="1em 0">
          {cartItems.length === 0 ? (
            <Box 
              textAlign="center" 
              py={8} 
              px={4}
              bg="rgba(130, 202, 156, 0.5)"
              borderRadius="lg"
            >
              <Text color="gray.500" fontSize="md">Your cart is empty</Text>
              <Text color="gray.400" fontSize="sm" mt={2}>Add items to your cart to continue</Text>
            </Box>
          ) : (
            <Stack spacing={4}>
              {cartItems.map((item, index) => (
                <Box 
                  key={index} 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center" 
                  p={4}
                  bg="rgba(130, 202, 156, 0.15)"
                  borderRadius="lg"
                  boxShadow="0 2px 6px rgba(0, 0, 0, 0.05)"
                  transition="all 0.2s"
                  _hover={{
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.08)",
                    transform: "translateY(-2px)"
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Flex
                      borderRadius="full"
                      width="50px"
                      height="50px"
                      bg="#82ca9c"
                      color="#fff"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      marginRight="1em"
                      boxShadow="0 2px 4px rgba(130, 202, 156, 0.3)"
                    >
                      <Text fontSize="sm" fontWeight="bold">{new Date(item.date).getDate()}</Text>
                      <Text fontSize="xs" fontWeight="medium">{new Date(item.date).toLocaleString('default', { month: 'short' })}</Text>
                    </Flex>
                    <Box>
                      <HStack spacing={2} mb={1}>
                        <Text fontSize="sm" color="gray.600">Location</Text>
                        <Text fontWeight="bold" fontSize="sm" color="gray.400">|</Text>
                        <Text fontSize="sm" fontWeight="medium" color="gray.700">Bay {item.bay}</Text>
                      </HStack>
                      <HStack spacing={2} mb={1}>
                        <TimeIcon color="#82ca9c" boxSize={3} />
                        <Text fontSize={["sm", "md"]} fontWeight="medium">{item.name}</Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.600">Price: <Text as="span" fontWeight="bold" color="gray.700">${item.price}</Text></Text>
                    </Box>
                  </Box>
                  <IconButton 
                    aria-label="Delete item" 
                    icon={<DeleteIcon />} 
                    onClick={() => handleRemoveItem(item)} 
                    variant="ghost"
                    colorScheme="red"
                    size="sm"
                    borderRadius="full"
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
      
      <CartSummaryPayment 
        total={total}
        cartItems={cartItems}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
        userProfile={userProfile}
      />
      
      <Box paddingBottom={150} width={["100%", "100%", "35%"]}>
      </Box>
    </Box>
  );
};

export default CartList;